/**
 * @file FooterLegalSettings.tsx
 * @description Вид нижней полосы подвала (phase19.16, приёмка Р2а; SB118 —
 * настройки выше нормы SB19; SB115; uiux-footer-columns §9): раскладка,
 * выравнивание, размер (меньше нормы нельзя), тон, разделитель, порядок
 * ссылок платформы, телефон и email по отдельности (хотя бы одно — ПП № 657
 * п. 22). Состав сведений о продавце под замком — его здесь нет.
 *
 * SB122 (uiux-settings-groups §3, §4.1): подгруппы уровня 2 «Вид полосы»,
 * «Ссылки», «Связь с продавцом»; подписи полей — `Label`. Место копирайта
 * (SB114) — `FooterCopyrightPlace`, его ставят сразу под строкой копирайта.
 *
 * Варианты — реестром `LEGAL_SEGMENTS` из подписей shared (новая настройка —
 * запись). Правку проверяет та же zod-схема, что и сервер
 * (`siteFooterLegalSchema`): ошибка показывается у поля, правка не уходит.
 * Изменения пишутся в черновик оформления, как вся тема.
 *
 * Партия «Юридические документы» (SB142): подгруппа «Ссылки» —
 * `FooterLinksGroup` (ссылки платформы и свои строки вперемешку,
 * «+ Добавить ссылку на документ»).
 */

import {
  FOOTER_COPYRIGHT_PLACE_OPTIONS,
  FOOTER_LEGAL_ALIGN_OPTIONS,
  FOOTER_LEGAL_DIVIDER_OPTIONS,
  FOOTER_LEGAL_LAYOUT_OPTIONS,
  FOOTER_LEGAL_SIZE_OPTIONS,
  FOOTER_LEGAL_TONE_OPTIONS,
  siteFooterLegalSchema,
  type FooterPlatformLink,
  type SiteFooterDocs,
  type SiteFooterLegal,
} from "@chaiberi/shared";
import { useState } from "react";

import { Label } from "../../ui/label";
import { FieldHint, SettingsSubgroup } from "../../ui/settings-groups";
import { CheckRow } from "../CheckRow";
import { Segmented } from "../Segmented";
import { FooterLinksGroup, type FooterLinksPatch } from "./FooterLinksGroup";
import type { LegalTarget } from "../../../lib/api-legal-targets";
import type { FooterLinkWhere } from "../../../lib/site-view/footer-link-where";

type SegKey = "copyrightPlace" | "layout" | "align" | "size" | "tone" | "divider";

interface SegmentDef<K extends SegKey> {
  key: K;
  label: string;
  options: Readonly<Record<SiteFooterLegal[K], string>>;
  /**
   * Короткие подписи для узкой панели (380) и телефона: длинный третий
   * вариант переносился на вторую строку. Полный текст — в подсказке и для
   * программ чтения экрана.
   */
  short?: Partial<Readonly<Record<SiteFooterLegal[K], string>>>;
  hint?: string;
}

type AnySegment = { [K in SegKey]: SegmentDef<K> }[SegKey];

/** Место копирайта — рядом со строкой копирайта (SB114, SB122 §4.1). */
const COPYRIGHT_SEGMENT: SegmentDef<"copyrightPlace"> = {
  key: "copyrightPlace",
  label: "Где копирайт",
  options: FOOTER_COPYRIGHT_PLACE_OPTIONS,
  short: { top: "Сверху", bottom: "Снизу", about: "В колонке" },
};

/** Реестр переключателей вида нижней полосы. */
const LEGAL_SEGMENTS: readonly AnySegment[] = [
  { key: "layout", label: "Раскладка", options: FOOTER_LEGAL_LAYOUT_OPTIONS, short: { inline: "В строку", columns: "Две колонки" } },
  { key: "align", label: "Выравнивание", options: FOOTER_LEGAL_ALIGN_OPTIONS },
  { key: "size", label: "Размер", options: FOOTER_LEGAL_SIZE_OPTIONS, hint: "Меньше нормы нельзя — сведения о продавце должны читаться." },
  {
    key: "tone",
    label: "Тон",
    options: FOOTER_LEGAL_TONE_OPTIONS,
    hint: "«Приглушённый» сайт покажет, только если текст читается (контраст от 4.5:1 к фону подвала); иначе — как текст подвала.",
  },
  { key: "divider", label: "Разделитель от колонок", options: FOOTER_LEGAL_DIVIDER_OPTIONS },
];

interface Props {
  value: SiteFooterLegal;
  onChange: (next: SiteFooterLegal) => void;
  /** Ссылки платформы, скрытые из-за дубля в меню подвала (SB115). */
  hiddenLinks: ReadonlySet<FooterPlatformLink>;
  /** «Где задаётся» каждая ссылка (SB121); нет — без подписей. */
  where?: Readonly<Record<FooterPlatformLink, FooterLinkWhere>>;
  /** Свои строки «Ссылок» (раздел темы `footerDocs`, SB142). */
  docs: SiteFooterDocs;
  /** Правка «Ссылок»: порядок платформы и свои строки — одной правкой черновика. */
  onLinks: (patch: FooterLinksPatch) => void;
  /** Документы и группы магазина; null — не загружены. */
  legalTargets: LegalTarget[] | null;
  onLegalReload?: () => void;
  /** `/shop/<slug>/settings/legal-documents`. */
  legalDocsHref: string;
}

function SegmentRow<K extends SegKey>({ seg, value, onChange }: { seg: SegmentDef<K>; value: SiteFooterLegal; onChange: Props["onChange"] }) {
  const options = (Object.keys(seg.options) as Array<SiteFooterLegal[K]>).map((v) => {
    const full = seg.options[v];
    const short = seg.short?.[v];
    return short ? { value: v, label: <span title={full}>{short}</span>, ariaLabel: full } : { value: v, label: full };
  });
  return (
    <div className="flex flex-col gap-2">
      <Label>{seg.label}</Label>
      <Segmented
        aria-label={`Нижняя полоса: ${seg.label.toLowerCase()}`}
        options={options}
        value={value[seg.key]}
        onChange={(v) => onChange({ ...value, [seg.key]: v })}
      />
      {seg.hint ? <FieldHint className="mt-0">{seg.hint}</FieldHint> : null}
    </div>
  );
}

/** «Где копирайт»: сверху, снизу полосы или в колонке «О магазине». */
export function FooterCopyrightPlace({ value, onChange }: Pick<Props, "value" | "onChange">) {
  return (
    <div className="flex flex-col gap-1">
      <SegmentRow seg={COPYRIGHT_SEGMENT} value={value} onChange={onChange} />
      {value.copyrightPlace === "about" ? (
        <FieldHint>Если колонки «О магазине» нет или подвал «в строку» — копирайт сверху полосы.</FieldHint>
      ) : null}
    </div>
  );
}

export function FooterLegalSettings({ value, onChange, hiddenLinks, where, docs, onLinks, legalTargets, onLegalReload, legalDocsHref }: Props) {
  const [contactError, setContactError] = useState<string | null>(null);

  // Телефон и email: правило «хотя бы одно» проверяет схема (как сервер).
  function setContact(field: "showPhone" | "showEmail", on: boolean) {
    const next = { ...value, [field]: on };
    const parsed = siteFooterLegalSchema.safeParse(next);
    if (!parsed.success) {
      setContactError(parsed.error.issues[0]?.message ?? "Покажите хотя бы одно: телефон или email продавца");
      return;
    }
    setContactError(null);
    onChange(next);
  }

  const error = contactError ?? (value.showPhone || value.showEmail ? null : "Покажите хотя бы одно: телефон или email продавца");

  return (
    <>
      <SettingsSubgroup title="Вид полосы">
        {LEGAL_SEGMENTS.map((seg) => (
          <SegmentRow key={seg.key} seg={seg as SegmentDef<SegKey>} value={value} onChange={onChange} />
        ))}
      </SettingsSubgroup>
      <FooterLinksGroup
        value={value}
        docs={docs}
        onLinks={onLinks}
        hiddenLinks={hiddenLinks}
        where={where}
        legalTargets={legalTargets}
        onLegalReload={onLegalReload}
        legalDocsHref={legalDocsHref}
      />
      <SettingsSubgroup title="Связь с продавцом">
        <div className="flex flex-col">
          <CheckRow checked={value.showPhone} onChange={(on) => setContact("showPhone", on)}>Телефон</CheckRow>
          <CheckRow checked={value.showEmail} onChange={(on) => setContact("showEmail", on)}>Email</CheckRow>
          {error ? (
            <p role="alert" className="type-caption mt-1 text-destructive">{error}</p>
          ) : (
            <FieldHint>По закону нужно хотя бы одно: телефон или email.</FieldHint>
          )}
        </div>
      </SettingsSubgroup>
    </>
  );
}
